/**
 * Network Adaptation Manager
 *
 * Automatically adjusts application settings based on detected network speed
 * to provide optimal experience across different connection qualities.
 *
 * Adaptation Strategy:
 * - Fast (>6 Mbps): No restrictions, full quality
 * - Medium (2-6 Mbps): 480p quality, max 5 media sinks
 * - Slow (<2 Mbps): 480p quality, disable video overlays, max 2 media sinks
 */

import { getBandwidthMonitor, type NetworkClassification } from './BandwidthMonitor'
import { setPreviewQuality } from './proxyResolution'
import { DEFAULT_MAX_VIDEO_SINKS, setMaxVideoSinks } from './gpuDetection'
import { setVideoOverlayEnabled } from './gpuDetection'

const STORAGE_KEY = 'martini-network-adaptation-settings'
const ADAPTATION_ENABLED_KEY = 'martini-network-adaptation-enabled'

/**
 * Settings that were in place before network adaptation kicked in.
 * Used to restore user preferences when connection improves.
 */
interface SavedSettings {
  quality: 'standard' | 'full'
  maxSinks: number
  overlayEnabled: boolean
}

/**
 * Current adaptation state
 */
interface AdaptationState {
  lastClassification: NetworkClassification
  savedSettings: SavedSettings | null
  adaptationApplied: boolean
}

const adaptationState: AdaptationState = {
  lastClassification: 'unknown',
  savedSettings: null,
  adaptationApplied: false,
}

let unsubscribeFromMonitor: (() => void) | null = null

/**
 * Check if network adaptation is enabled (default: true)
 */
export function isNetworkAdaptationEnabled(): boolean {
  if (typeof window === 'undefined') return true

  try {
    const stored = localStorage.getItem(ADAPTATION_ENABLED_KEY)
    if (stored !== null) {
      return stored === 'true'
    }
  } catch {
    // Ignore errors
  }
  return true // Default: enabled
}

/**
 * Save current settings before applying adaptation
 */
function saveCurrentSettings(): void {
  if (typeof window === 'undefined') return

  try {
    const quality = localStorage.getItem('martini-preview-quality') as 'standard' | 'full' | null
    const maxSinks = localStorage.getItem('martini_max_video_sinks')
    const overlayEnabled = localStorage.getItem('martini_video_overlay_enabled')

    adaptationState.savedSettings = {
      quality: quality || 'full',
      maxSinks: maxSinks ? parseInt(maxSinks, 10) : DEFAULT_MAX_VIDEO_SINKS,
      overlayEnabled: overlayEnabled !== 'false',
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(adaptationState.savedSettings))
    console.log('[NetworkAdaptation] Saved current settings:', adaptationState.savedSettings)
  } catch (error) {
    console.error('[NetworkAdaptation] Failed to save settings:', error)
  }
}

/**
 * Restore previously saved settings
 */
function restoreSettings(): void {
  if (!adaptationState.savedSettings) {
    // Try to load from localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        adaptationState.savedSettings = JSON.parse(stored)
      }
    } catch {
      // Ignore errors
    }
  }

  if (adaptationState.savedSettings) {
    console.log('[NetworkAdaptation] Restoring saved settings:', adaptationState.savedSettings)
    setPreviewQuality(adaptationState.savedSettings.quality)
    setMaxVideoSinks(adaptationState.savedSettings.maxSinks)
    setVideoOverlayEnabled(adaptationState.savedSettings.overlayEnabled)
    adaptationState.adaptationApplied = false
    adaptationState.savedSettings = null
    localStorage.removeItem(STORAGE_KEY)
  }
}

/**
 * Apply network-specific adaptations
 */
function applyAdaptation(classification: NetworkClassification): void {
  if (!isNetworkAdaptationEnabled()) {
    console.log('[NetworkAdaptation] Auto-adaptation disabled, skipping')
    return
  }

  // Skip if classification hasn't changed
  if (classification === adaptationState.lastClassification) {
    return
  }

  console.log(
    `[NetworkAdaptation] Network classification changed: ${adaptationState.lastClassification} → ${classification}`,
  )
  adaptationState.lastClassification = classification

  switch (classification) {
    case 'slow':
      // Save settings before first adaptation
      if (!adaptationState.adaptationApplied) {
        saveCurrentSettings()
      }
      console.log('[NetworkAdaptation] Applying slow network mode: 480p, overlays OFF, 2 sinks')
      setPreviewQuality('standard') // 480p
      setVideoOverlayEnabled(false) // Disable overlays
      setMaxVideoSinks(2) // Reduce to 2 sinks
      adaptationState.adaptationApplied = true
      break

    case 'medium':
      // Save settings before first adaptation
      if (!adaptationState.adaptationApplied) {
        saveCurrentSettings()
      }
      console.log('[NetworkAdaptation] Applying medium network mode: 480p, overlays ON, 5 sinks')
      setPreviewQuality('standard') // 480p
      setVideoOverlayEnabled(true) // Keep overlays enabled
      setMaxVideoSinks(5) // Moderate limit
      adaptationState.adaptationApplied = true
      break

    case 'fast':
      // Connection improved - restore original settings if any exist
      // Check both in-memory flag and localStorage for saved settings
      const hasSavedSettings =
        adaptationState.adaptationApplied ||
        (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY) !== null)

      if (hasSavedSettings) {
        console.log('[NetworkAdaptation] Fast connection detected, restoring original settings')
        restoreSettings()
      }
      break

    case 'unknown':
      // Don't apply adaptations for unknown state
      console.log('[NetworkAdaptation] Network speed unknown, no adaptation applied')
      break
  }
}

/**
 * Initialize network adaptation monitoring.
 * Should be called once during app initialization.
 */
export function initializeNetworkAdaptation(): void {
  if (unsubscribeFromMonitor) {
    // Already initialized
    return
  }

  const monitor = getBandwidthMonitor()

  // Apply initial adaptation based on current classification
  applyAdaptation(monitor.classification)

  // Subscribe to classification changes and store the unsubscribe function
  unsubscribeFromMonitor = monitor.subscribe(classification => {
    applyAdaptation(classification)
  })

  console.log('[NetworkAdaptation] Monitoring initialized')
}

/**
 * Stop network adaptation monitoring.
 * Should be called during app cleanup.
 */
export function stopNetworkAdaptation(): void {
  if (unsubscribeFromMonitor) {
    unsubscribeFromMonitor()
    unsubscribeFromMonitor = null
    console.log('[NetworkAdaptation] Monitoring stopped')
  }
}
